import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ImageIcon from '@mui/icons-material/Image';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchProjectById,
  deleteProject,
  uploadProjectCoverImage,
  uploadProjectImage,
  reorderProjectImages,
  removeProjectImage,
  addProjectMember,
  removeProjectMember,
  setProjectLeader,
} from '../controllers/projectsController';
import { searchMembersWithSubMembers } from '../controllers/memberManagementController';
import { resolveProjectsAssetUrl } from '../api/httpClientProjects';
import { resolveGroupsAssetUrl } from '../api/httpClientGroups';
import BackButton from '../components/BackButton';
import EditProjectDialog from '../components/EditProjectDialog';

const statusOptions = [
  { value: 0, label: 'Planlandı' },
  { value: 1, label: 'Devam Ediyor' },
  { value: 2, label: 'Tamamlandı' },
  { value: 3, label: 'Beklemede' },
  { value: 4, label: 'İptal' },
];

const statusLabel = (status) => {
  return statusOptions.find((opt) => opt.value === status)?.label || 'Bilinmiyor';
};

const statusColor = (status) => {
  switch (status) {
    case 0:
      return '#0E4D94';
    case 1:
      return '#FF9800';
    case 2:
      return '#17C653';
    case 3:
      return '#FFA500';
    case 4:
      return '#F8285A';
    default:
      return '#666666';
  }
};

const statusBackgroundColor = (status) => {
  switch (status) {
    case 0:
      return '#E7EDF4';
    case 1:
      return '#FFF4E6';
    case 2:
      return '#DFFFEA';
    case 3:
      return '#FFF4E6';
    case 4:
      return '#FFEEF3';
    default:
      return '#f5f5f5';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} - ${hours}:${minutes}`;
};

const SortableGalleryImage = ({ id, url }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };
  return (
    <Grid item ref={setNodeRef} style={style} {...attributes} {...listeners} xs={12}>
      <Paper variant="outlined" sx={{ width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e6e6e6' }}>
        <Box component="img" src={resolveProjectsAssetUrl(url)} alt={id} sx={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      </Paper>
    </Grid>
  );
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  // Cover image
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Gallery
  const [galleryImages, setGalleryImages] = useState([]);
  const [removeImageDialogOpen, setRemoveImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Members
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [memberWarning, setMemberWarning] = useState('');

  // Edit mode
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchProjectById(id);
        if (!active) return;
        setItem(data);
        setGalleryImages(Array.isArray(data?.gallery) ? data.gallery : []);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Proje alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  // Member search
  useEffect(() => {
    if (!memberQuery || memberQuery.length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembersWithSubMembers(memberQuery)
      .then((data) => {
        if (!active) return;
        const flattened = [];
        (Array.isArray(data) ? data : []).forEach((item) => {
          if (!item) return;
          // Eğer direkt olarak type property'si varsa (flat array), onu kullan
          if (typeof item.type === 'number') {
            flattened.push({
              id: item.id,
              fullName: item.fullName,
              profilePictureUrl: item.profilePictureUrl,
              type: item.type === 1 ? 'SubMember' : 'Member',
              parentMemberId: item.parentMemberId || null,
            });
          } else {
            // Eğer nested yapı varsa (member içinde subMembers), eski mantık
            flattened.push({
              id: item.id,
              fullName: item.fullName,
              profilePictureUrl: item.profilePictureUrl,
              type: 'Member',
            });
            if (Array.isArray(item.subMembers)) {
              item.subMembers.forEach((s) => {
                if (!s) return;
                flattened.push({
                  id: s.id,
                  fullName: s.fullName,
                  profilePictureUrl: s.profilePictureUrl,
                  type: 'SubMember',
                  parentMemberId: item.id,
                  parentFullName: item.fullName,
                });
              });
            }
          }
        });
        setMemberOptions(flattened);
      })
      .catch(() => {
        if (!active) return;
        setMemberOptions([]);
      })
      .finally(() => {
        if (active) setMemberLoading(false);
      });
    return () => {
      active = false;
    };
  }, [memberQuery]);

  // Cleanup preview URLs
  useEffect(() => {
    return () => {
      if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    };
  }, [coverPreviewUrl]);

  const loadProject = async () => {
    try {
      const data = await fetchProjectById(id);
      setItem(data);
      setGalleryImages(Array.isArray(data?.gallery) ? data.gallery : []);
    } catch (e) {
      setError(e?.message || 'Proje alınamadı');
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const url = URL.createObjectURL(file);
    setCoverPreviewUrl(url);
  };

  const handleUploadCover = async () => {
    if (!coverFile || !item?.id) return;
    setUploadingCover(true);
    try {
      await uploadProjectCoverImage({ projectId: item.id, file: coverFile });
      await loadProject();
      if (coverPreviewUrl) {
        URL.revokeObjectURL(coverPreviewUrl);
      }
      setCoverFile(null);
      setCoverPreviewUrl(null);
    } catch (e) {
      alert(e?.message || 'Kapak görseli yüklenemedi');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAddImage = async (file) => {
    if (!file || !item?.id) return;
    setUploadingImage(true);
    try {
      const added = await uploadProjectImage({ projectId: item.id, file });
      setGalleryImages((prev) => [...prev, added]);
    } catch (e) {
      alert(e?.message || 'Görsel yüklenemedi');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!selectedImage || !item?.id) return;
    try {
      await removeProjectImage({ projectId: item.id, imageId: selectedImage.id });
      setGalleryImages((prev) => prev.filter((img) => img.id !== selectedImage.id));
      setRemoveImageDialogOpen(false);
      setSelectedImage(null);
    } catch (e) {
      alert(e?.message || 'Görsel kaldırılamadı');
    }
  };

  const handleGalleryDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = galleryImages.findIndex((i) => i.id === active.id);
    const newIndex = galleryImages.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(galleryImages, oldIndex, newIndex);
    setGalleryImages(newOrder);
    try {
      await reorderProjectImages({
        projectId: item.id,
        imageIdsInOrder: newOrder.map((i) => i.id),
      });
    } catch (e) {
      // Revert on error
      setGalleryImages(galleryImages);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedMembers || selectedMembers.length === 0 || !item?.id) return;

    setMemberWarning('');
    setAddingMember(true);
    try {
      // Filter out already added members
      const newMembers = selectedMembers.filter((selectedMember) => {
        const isSubMember = selectedMember.type === 'SubMember';
        const memberType = isSubMember ? 1 : 0;
        const existingMember = Array.isArray(item.groupMembers)
          ? item.groupMembers.find(
            (m) => m.id === selectedMember.id && m.memberType === memberType
          )
          : null;
        return !existingMember;
      });

      if (newMembers.length === 0) {
        setMemberWarning('Seçilen üyeler zaten projeye dahil.');
        setTimeout(() => setMemberWarning(''), 3000);
        setAddingMember(false);
        return;
      }

      // Add all new members
      const addPromises = newMembers.map((selectedMember) => {
        const isSubMember = selectedMember.type === 'SubMember';
        const memberType = isSubMember ? 1 : 0;
        return addProjectMember({
          projectId: item.id,
          memberId: selectedMember.id,
          memberType,
          fullName: selectedMember.fullName,
          profilePictureUrl: selectedMember.profilePictureUrl || null,
          role: null,
          isLeader: false,
          parentMemberId: isSubMember ? selectedMember.parentMemberId || null : null,
        });
      });

      await Promise.all(addPromises);
      await loadProject();
      setSelectedMembers([]);
      setMemberQuery('');
    } catch (e) {
      alert(e?.message || 'Üyeler eklenemedi');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!item?.id) return;
    try {
      await removeProjectMember({
        projectId: item.id,
        memberId: member.id,
        memberType: member.memberType ?? 0,
      });
      await loadProject();
    } catch (e) {
      alert(e?.message || 'Üye kaldırılamadı');
    }
  };

  const handleSetLeader = async (member) => {
    if (!item?.id) return;
    try {
      await setProjectLeader({
        projectId: item.id,
        memberId: member.id,
        memberType: member.memberType ?? 0,
      });
      await loadProject();
    } catch (e) {
      alert(e?.message || 'Proje lideri belirlenemedi');
    }
  };

  const handleDelete = async () => {
    if (!item?.id) return;
    setDeleteConfirming(true);
    try {
      await deleteProject(item.id);
      navigate('/projects');
    } catch (e) {
      alert(e?.message || 'Proje silinemedi');
      setDeleteConfirming(false);
    }
  };

  const leader = useMemo(() => {
    return Array.isArray(item?.groupMembers) ? item.groupMembers.find((m) => m.isLeader) : null;
  }, [item?.groupMembers]);

  const sortedMembers = useMemo(() => {
    if (!Array.isArray(item?.groupMembers)) return [];
    // Lideri en üste taşı, diğerlerini olduğu gibi bırak
    const members = [...item.groupMembers];
    return members.sort((a, b) => {
      const aIsLeader = a.isLeader || false;
      const bIsLeader = b.isLeader || false;
      if (aIsLeader && !bIsLeader) return -1;
      if (!aIsLeader && bIsLeader) return 1;
      return 0;
    });
  }, [item?.groupMembers]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!item) return null;

  return (
    <Stack spacing={2}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: 500 }}>{item.name}</Typography>
        <Stack direction="row" spacing={1}>
          {!editMode && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={() => setEditMode(true)}
                startIcon={<EditIcon sx={{ width: 16 }} />}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E7EDF4',
                  boxShadow: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  color: '#0E4D94',
                  '&:hover': {
                    backgroundColor: '#CFDBEA',
                    boxShadow: 'none',
                  },
                }}
              >
                Düzenle
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                }}
              >
                Sil
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none' }}>
        <Stack spacing={3}>
          {/* Kapak Görseli ve Bilgiler */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '.75fr 1fr' }, gap: 5, alignItems: 'start' }}>
            {/* Sol Taraf - Kapak Görseli ve Galeri */}
            <Box>
              <Stack spacing={2}>
                {/* Kapak Görseli */}
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>Kapak Görseli</Typography>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="cover-image-update"
                      type="file"
                      onChange={handleCoverImageChange}
                    />
                    {coverFile ? (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleUploadCover}
                        disabled={uploadingCover}
                        sx={{
                          textTransform: 'none',
                          fontSize: 13,
                          borderRadius: '5px',
                          px: 1,
                        }}
                      >
                        {uploadingCover ? 'Yükleniyor...' : 'Kaydet'}
                      </Button>
                    ) : (
                      <Button
                        component="label"
                        htmlFor="cover-image-update"
                        size="small"
                        startIcon={uploadingCover ? <CircularProgress size={14} sx={{ color: '#0E4D94' }} /> : <EditIcon sx={{ width: 16 }} />}
                        disabled={uploadingCover}
                        sx={{
                          textTransform: 'none',
                          backgroundColor: '#E7EDF4',
                          boxShadow: 'none',
                          fontSize: 13,
                          borderRadius: '5px',
                          px: 1,
                          color: '#0E4D94',
                          '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {uploadingCover ? 'Güncelleniyor...' : item.coverImageUrl ? 'Güncelle' : 'Ekle'}
                      </Button>
                    )}
                  </Stack>
                  <Box sx={{ position: 'relative', width: '100%', bgcolor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e6e6e6', minHeight: 300, maxHeight: 400 }}>
                    {coverPreviewUrl ? (
                      <Box
                        component="img"
                        src={coverPreviewUrl}
                        alt="Kapak Görseli"
                        sx={{
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : item.coverImageUrl ? (
                      <Box
                        component="img"
                        src={resolveProjectsAssetUrl(item.coverImageUrl)}
                        alt="Kapak Görseli"
                        sx={{
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                    )}
                  </Box>
                </Box>

                {/* Galeri */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', color: '#444444', mb: 1.5 }}>Galeri</Typography>
                  {galleryImages.length > 0 ? (
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
                      <SortableContext items={galleryImages.map((i) => i.id)} strategy={rectSortingStrategy}>
                        <Grid container spacing={1}>
                          {galleryImages.map((img, index) => (
                            <Grid key={img.id} item xs={12} sx={{ position: 'relative' }}>
                              <Box sx={{ position: 'relative' }}>
                                <SortableGalleryImage id={img.id} url={img.url} />
                                <Tooltip title="Kaldır">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    sx={{ position: 'absolute', top: 4, right: 4 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImage(img);
                                      setRemoveImageDialogOpen(true);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>
                          ))}
                          {/* Görsel Ekle Kartı */}
                          <Grid item xs={12}>
                            <Box
                              component="label"
                              htmlFor="add-image-input"
                              sx={{
                                width: 90,
                                height: 90,
                                border: '2px dashed #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                backgroundColor: '#fafafa',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#0E4D94',
                                  backgroundColor: '#f5f5f5',
                                },
                              }}
                            >
                              <input
                                id="add-image-input"
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleAddImage(file);
                                  }
                                  e.target.value = '';
                                }}
                                disabled={uploadingImage}
                              />
                              <AddPhotoAlternateIcon sx={{ fontSize: '24px', color: '#0E4D94' }} />
                              <Typography variant="caption" sx={{ fontSize: '11px', color: '#0E4D94', fontWeight: 500 }}>
                                Görsel Ekle
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <Box
                      component="label"
                      htmlFor="add-image-input-empty"
                      sx={{
                        width: 90,
                        height: 90,
                        border: '2px dashed #e6e6e6',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        backgroundColor: '#fafafa',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#0E4D94',
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      <input
                        id="add-image-input-empty"
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleAddImage(file);
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingImage}
                      />
                      <AddPhotoAlternateIcon sx={{ fontSize: '24px', color: '#0E4D94' }} />
                      <Typography variant="caption" sx={{ fontSize: '11px', color: '#0E4D94', fontWeight: 500 }}>
                        Görsel Ekle
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>

            {/* Sağ Taraf - Bilgiler */}
            <Box sx={{ marginTop: '50px!important' }}>
              <Stack spacing={2}>
                {/* Durum - Aktif/Pasif */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <CategoryIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Proje Durumu</Typography>
                      <Chip
                        label={statusLabel(item.status)}
                        size="small"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: statusBackgroundColor(item.status),
                          color: statusColor(item.status),
                          borderRadius: '12px',
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {item.isActive ? (
                      <CheckCircleIcon sx={{ fontSize: '20px', color: '#17C653', mt: 0.5 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: '20px', color: '#F8285A', mt: 0.5 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Durum</Typography>
                      <Chip
                        label={item.isActive ? 'Aktif' : 'Pasif'}
                        size="small"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: item.isActive ? '#DFFFEA' : '#FFEEF3',
                          color: item.isActive ? '#17C653' : '#F8285A',
                          height: 24,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Tarih - Tema */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <ScheduleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Tarih</Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444', mb: 0.25 }}>
                        {item.startDate ? formatDate(item.startDate) : '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#a3a3a3' }}>
                        {item.endDate ? formatDate(item.endDate) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <CategoryIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Tema</Typography>
                      <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                        {item.theme || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Amaç - Lider */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <CategoryIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Amaç</Typography>
                      <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                        {item.purpose || '-'}
                      </Typography>
                    </Box>
                  </Box>
                  {leader && (
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <PersonIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Lider</Typography>
                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                          {leader.fullName}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <AccountBalanceIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Logo cari kod</Typography>
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', fontVariantNumeric: 'tabular-nums' }}>
                      {item.logoCariCode || '-'}
                    </Typography>
                  </Box>
                </Box>

                {/* Açıklama */}
                {item.description && (
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Açıklama</Typography>
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 400, color: '#444444', whiteSpace: 'pre-wrap' }}>
                      {item.description}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#e6e6e6' }} />

          {/* Proje Ekibi */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>Proje Ekibi</Typography>
              {sortedMembers.length > 0 && (
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                  {sortedMembers.length} üye
                </Typography>
              )}
            </Stack>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Autocomplete
                  multiple
                  sx={{
                    flex: 1,
                  }}
                  options={memberOptions}
                  getOptionLabel={(o) => o.fullName || ''}
                  value={selectedMembers}
                  onChange={(e, v) => setSelectedMembers(v || [])}
                  onInputChange={(e, v) => setMemberQuery(v)}
                  loading={memberLoading}
                  size="small"
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionDisabled={(option) => {
                    const isSubMember = option.type === 'SubMember';
                    const memberType = isSubMember ? 1 : 0;
                    return Array.isArray(item.groupMembers)
                      ? item.groupMembers.some((m) => m.id === option.id && m.memberType === memberType)
                      : false;
                  }}
                  renderOption={(props, option) => {
                    const isSubMember = option.type === 'SubMember';
                    const memberType = isSubMember ? 1 : 0;
                    const isDisabled = Array.isArray(item.groupMembers)
                      ? item.groupMembers.some((m) => m.id === option.id && m.memberType === memberType)
                      : false;
                    return (
                      <Box
                        component="li"
                        {...props}
                        key={option.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                      >
                        <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <Typography variant="body2">{option.fullName}</Typography>
                          {option.type === 'SubMember' && option.parentFullName && (
                            <Typography variant="caption" color="text.secondary">
                              Üye: {option.parentFullName}
                            </Typography>
                          )}
                        </Box>
                        {isDisabled && <Typography variant="caption" sx={{ ml: 'auto', color: '#a3a3a3' }}>(Zaten ekli)</Typography>}
                      </Box>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.fullName}
                        avatar={<Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 20, height: 20 }} />}
                        size="small"
                        sx={{
                          backgroundColor: '#E7EDF4',
                          color: '#0E4D94',
                          fontSize: '11px',
                          height: '24px',
                          '& .MuiChip-deleteIcon': {
                            fontSize: '14px',
                            color: '#0E4D94',
                            '&:hover': {
                              color: '#0a3d7a',
                            },
                          },
                        }}
                      />
                    ))
                  }
                  slotProps={{ paper: { sx: { width: 560 } }, listbox: { sx: { maxHeight: 360 } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedMembers.length > 0 ? `${selectedMembers.length} üye seçildi` : 'Üye ara ve ekle...'}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          backgroundColor: '#fafafa',
                          minHeight: '36px',
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                              borderWidth: '1px',
                            },
                          },
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          py: selectedMembers.length > 0 ? '2px' : '4px',
                          px: '10px',
                        },
                        '& .MuiAutocomplete-input': {
                          py: 0,
                          px: 0,
                          fontSize: '13px',
                        },
                        '& .MuiInputBase-input': {
                          py: 0.8,
                          px: 1,
                          fontSize: '13px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                          borderWidth: '1px',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {memberLoading ? <CircularProgress color="inherit" size={14} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <IconButton
                  onClick={handleAddMembers}
                  disabled={selectedMembers.length === 0 || addingMember}
                  sx={{
                    backgroundColor: selectedMembers.length > 0 && !addingMember ? '#0E4D94' : '#e6e6e6',
                    color: selectedMembers.length > 0 && !addingMember ? '#ffffff' : '#a3a3a3',
                    borderRadius: '5px',
                    width: 36,
                    height: 36,
                    mt: 0.5,
                    '&:hover': {
                      backgroundColor: selectedMembers.length > 0 && !addingMember ? '#0a3d7a' : '#e6e6e6',
                    },
                    '&:disabled': {
                      backgroundColor: '#e6e6e6',
                      color: '#a3a3a3',
                    },
                  }}
                >
                  {addingMember ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Stack>
            </Box>
            {memberWarning && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setMemberWarning('')}>
                {memberWarning}
              </Alert>
            )}
            {sortedMembers.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {sortedMembers.map((m) => {
                  const isLeader = m.isLeader || false;
                  return (
                    <Card
                      key={m.id}
                      sx={{
                        p: 2,
                        border: '1px solid #e6e6e6',
                        boxShadow: 'none',
                        borderRadius: '8px',
                        position: 'relative',
                        '&:hover': {
                          borderColor: '#0E4D94',
                          boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8 }}>
                        {!isLeader && (
                          <Tooltip title="Lider Yap">
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleSetLeader(m);
                              }}
                              sx={{
                                p: 0.5,
                                backgroundColor: 'transparent',
                                width: 24,
                                height: 24,
                                '&:hover': {
                                  backgroundColor: '#FFF8E1',
                                },
                                '& svg': {
                                  fontSize: 14,
                                  color: '#FFA500',
                                },
                              }}
                            >
                              <StarBorderIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Kaldır">
                          <IconButton
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleRemoveMember(m);
                            }}
                            sx={{
                              backgroundColor: '#FFEEF3',
                              borderRadius: '6px',
                              width: 24,
                              height: 24,
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '14px',
                                color: '#F8285A',
                              },
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={resolveGroupsAssetUrl(m.profilePictureUrl)}
                          sx={{
                            width: 48,
                            height: 48,
                            border: '2px solid #e6e6e6',
                          }}
                          onClick={() => {
                            if (m.memberType === 1 && m.parentMemberId) {
                              navigate(`/members/${m.parentMemberId}`);
                            } else {
                              navigate(`/members/${m.id}`);
                            }
                          }}
                        >
                          {!m.profilePictureUrl && (
                            <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                              {m.fullName?.charAt(0) || '?'}
                            </Typography>
                          )}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#444444',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {m.fullName}
                            </Typography>
                            {isLeader && (
                              <Tooltip title="Proje Lideri">
                                <StarIcon sx={{ color: '#FFA500', fontSize: 16, flexShrink: 0 }} />
                              </Tooltip>
                            )}
                          </Stack>
                          {m.role && (
                            <Typography variant="caption" sx={{ fontSize: '12px', color: '#a3a3a3' }}>
                              {m.role}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px',
                  backgroundColor: '#fafafa',
                  border: '2px dashed #e6e6e6',
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                  Henüz üye eklenmemiş
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Card>

      <Divider sx={{ borderColor: '#e6e6e6' }} />
      <Stack spacing={0.5} sx={{ mt: -0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
          Oluşturulma Tarihi: {item.createdAtUtc ? formatDate(item.createdAtUtc) : '-'}
        </Typography>
      </Stack>

      {/* Remove Image Dialog */}
      <Dialog open={removeImageDialogOpen} onClose={() => setRemoveImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Görseli Kaldır
          <IconButton
            onClick={() => setRemoveImageDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            Bu görseli kaldırmak istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setRemoveImageDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleRemoveImage}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#d91e47',
              },
            }}
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <EditProjectDialog
        open={editMode}
        onClose={() => setEditMode(false)}
        project={item}
        onSuccess={loadProject}
      />

      {/* Delete Project Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Projeyi Sil
          <IconButton
            onClick={() => setDeleteOpen(false)}
            size="small"
            disabled={deleteConfirming}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            Bu projeyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleteConfirming}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteConfirming}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {deleteConfirming ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ProjectDetail;
